import { useContext, useEffect, useReducer, useRef, useState } from 'react';
import styles from './Settings.module.css';
import AIEduTabMenu from '../../components/TabMenu/AIEduTabMenu';
import OrganizationDetails from './OrganizationDetails';
import AdminSettings from './AdminSettings';
import { Context } from '../../context/Context';
import { API } from '../../services';
import AIEduToast from '../../components/Toast/AIEduToast';
import {
    setToastComponent,
    showErrorToast,
    showSuccessToast,
} from '../../services/setup/ApiResponseHandler';
import AIEduSpinner from '../../components/Spinner/AIEduSpinner';
import SettingsFormReducer from './reducer/SettingsFormReducer';
import { trimObjectValues } from '../../utils/Utils';
import userIcon from '../../assets/images/profilepic.png';
import { useAuthenticate } from '../../hooks';
import { ROLE_USER } from '../../utils/Constants';
import { useIntl } from 'react-intl';
import microsoftIcon from '../../assets/images/microsoft-icon.svg';
import googleIcon from '../../assets/images/google-icon.svg';

const Settings = () => {
    const intl = useIntl();
    const { formatMessage: f } = intl;
    const { accountInfo } = useAuthenticate();
    const { account, linkedAccount } = accountInfo ?? {};
    const { userName, email, userLogoUrl } = account ?? {};

    const {
        settingsCurrentActive,
        setSettingsCurrentActive,
        setSideMenuCurrentActive,
    } = useContext(Context);

    const toastRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const initialFormState = useRef({
        organizationName: '',
        address: '',
        city: '',
        country: '',
        state: '',
        zipCode: '',
        approvalRole: ROLE_USER,
        approvalType: true,
    });

    const [formState, setFormState] = useReducer(
        SettingsFormReducer,
        initialFormState.current
    );

    const getSettingsDetails = () => {
        setLoading(true);
        API.getOrganizationDetails()
            .then(({ data }) => {
                const { organization } = data;
                Object.keys(initialFormState.current).forEach((key) => {
                    if (key === 'approvalType') {
                        initialFormState.current[key] =
                            organization?.[key] === 'AUTO' ? true : false;
                    } else if (key === 'approvalRole') {
                        initialFormState.current[key] =
                            organization?.[key] || ROLE_USER;
                    } else {
                        initialFormState.current[key] =
                            organization?.[key] || '';
                    }
                });

                setFormState({
                    type: 'SET_ALL_FIELDS',
                    payload: initialFormState.current,
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        setSideMenuCurrentActive(-1);
        setToastComponent(toastRef.current);

        getSettingsDetails();
    }, []);

    const handleFormUpdate = () => {
        const payload = trimObjectValues(formState);
        const title = items[settingsCurrentActive].header;

        setLoading(true);
        API.updateOrganization({
            ...payload,
            approvalType: payload.approvalType ? 'AUTO' : 'MANUAL',
        })
            .then((response) => {
                if (response.status === 200) {
                    showSuccessToast(`${title} updated successfully.`);
                }
            })
            .finally(() => {
                getSettingsDetails();
                setLoading(false);
            });
    };

    const handleChange = (e) => {
        setFormState({
            type: 'SET_INPUT',
            field: e.target.name,
            payload: e.target.value,
        });
    };

    const handleFormReset = () => {
        setFormState({
            type: 'SET_ALL_FIELDS',
            payload: initialFormState.current,
        });
    };

    const items = [
        {
            header: f({ id: 'AI_EDU_ORG_DETAILS' }),
            data: (
                <OrganizationDetails
                    f={f}
                    formState={formState}
                    handleFormUpdate={handleFormUpdate}
                    handleChange={handleChange}
                    handleFormReset={handleFormReset}
                />
            ),
        },
        {
            header: f({ id: 'AI_EDU_ADMIN_SETTINGS' }),
            data: (
                <AdminSettings
                    f={f}
                    formState={formState}
                    handleFormUpdate={handleFormUpdate}
                    handleChange={handleChange}
                    handleFormReset={handleFormReset}
                />
            ),
        },
    ];

    const onTabChange = (e) => {
        setSettingsCurrentActive(e.index);
        getSettingsDetails();
    };

    const ACCOUNTS = {
        microsoft: {
            icon: microsoftIcon,
            text: f({ id: 'AI_EDU_LINKED_VIA_MICROSOFT' }),
        },
        google: {
            icon: googleIcon,
            text: f({ id: 'AI_EDU_LINKED_VIA_GOOGLE' }),
        },
    };

    const LINKED_ACCOUNT = {
        icon: ACCOUNTS?.[linkedAccount]?.icon,
        text: ACCOUNTS?.[linkedAccount]?.text,
    };

    return (
        <>
            {loading && <AIEduSpinner />}
            <div className={styles.settings_layout}>
                <AIEduToast ref={toastRef} />
                <div className={styles.user_info}>
                    <div className={styles.profile}>
                        <div className={styles.icon}>
                            <img src={userLogoUrl ? userLogoUrl : userIcon} />
                        </div>
                        <div className={styles.user_details}>
                            <div className={styles.title} title={userName}>
                                {userName}
                            </div>
                            <div className={styles.subtitle} title={email}>
                                {email}
                            </div>
                        </div>
                    </div>
                    <div className={styles.link_account}>
                        <div className={styles.account_logo}>
                            <img src={LINKED_ACCOUNT.icon} />
                        </div>
                        {LINKED_ACCOUNT.text}
                    </div>
                </div>
                <div
                    className={styles.divider}
                    style={{ marginTop: '1.5rem', marginBottom: '1rem' }}
                />
                <AIEduTabMenu
                    items={items}
                    activeIndex={settingsCurrentActive}
                    onTabChange={onTabChange}
                />
            </div>
        </>
    );
};

export default Settings;
